import React from "react";
import { Box, Typography, Paper, Chip, Stack, Divider } from "@mui/material";
import { AccessTime, CalendarMonth } from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";

import horarioBgImg from '../../assets/images/horarios.png';

// Orden de visualización: Lunes (1) a Domingo (0)
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export default function PublicScheduleViewer({ schedules = [] }) {
    const { t } = useTranslation();
    const theme = useTheme();

    // Obtenemos el día actual (0-6) para resaltarlo
    const currentDay = new Date().getDay();

    // Diccionario de días (puedes usar tus keys de traducción aquí)
    const dayLabels = {
        1: "Lunes",
        2: "Martes",
        3: "Miércoles",
        4: "Jueves",
        5: "Viernes",
        6: "Sábado",
        0: "Domingo"
    };

    // --- PROCESAMIENTO DE DATA ---
    // Agrupamos los horarios crudos por día para manejar turnos partidos
    const processSchedule = () => {
        return DAY_ORDER.map(dayId => {
            // Filtramos los turnos que corresponden a este día
            // y que NO sean día libre explícito (aunque nuestra lógica backend ya los filtra, aseguramos)
            const shifts = schedules
                .filter(s => s.day_of_week == dayId && !s.is_day_off)
                .sort((a, b) => a.start_time.localeCompare(b.start_time)); // Ordenar mañana -> tarde

            return {
                id: dayId,
                label: t(`days.${dayId}`), 
                isOpen: shifts.length > 0,
                shifts: shifts
            };
        });
    };

    const weeklySchedule = processSchedule();

    return (
        <Box sx={{ width: '100%', position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
            
            {/* Título */}
            <Box display="flex" alignItems="center" gap={1} mb={2} pl={2}>
                
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {t('helpers.workingHours') || "Horarios de Atención"}
                </Typography>
            </Box>

            {/* Contenedor Principal con Fondo */}
            
                <Box sx={{ 
                    p: 2, 
                    position: 'relative', 
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(4px)' // Efecto moderno "glass"
                }}>
                {/* Imagen de fondo decorativa (restaurada) */}
                <Box
                    sx={{
                        backgroundImage: `url(${horarioBgImg})`,
                        opacity: 0.08, // Muy sutil
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'contain',
                        position: 'absolute',
                        right: 0, left: 0, top: 0, bottom: 0,
                        zIndex: -1,
                        pointerEvents: 'none'
                    }}
                />

                <Stack spacing={1.5}>
                    {weeklySchedule.map((day) => {
                        const isToday = currentDay === day.id;

                        return (
                            <Box 
                                key={day.id} 
                                sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center', // Centrado vertical para turnos múltiples
                                    p: 1,
                                    borderRadius: 1,
                                    // Resaltar el día de hoy
                                    bgcolor: isToday ? alpha(theme.palette.link.main, 0.1) : 'transparent',
                                    border: isToday ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : 'none'
                                }}
                            >
                                {/* Nombre del Día */}
                                <Typography 
                                    variant="body1" 
                                    fontWeight={isToday ? 'bold' : 'normal'}
                                    color={isToday ? 'primary.main' : 'text.primary'}
                                    sx={{ minWidth: 100 }}
                                >
                                    {day.label} {isToday && <Typography component="span" variant="caption" color="primary" sx={{ml:1}}>({t('helpers.today')})</Typography>}
                                </Typography>

                                {/* Horas o Cerrado */}
                                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    {day.isOpen ? (
                                        day.shifts.map((shift, index) => (
                                            <Typography key={index} variant="body2" sx={{ fontSize: '0.95rem' }}>
                                                {/* Cortamos los segundos: 08:00:00 -> 08:00 */}
                                                {shift.start_time.substring(0, 5)} - {shift.end_time.substring(0, 5)}
                                            </Typography>
                                        ))
                                    ) : (
                                        <Chip 
                                            label={t('helpers.closed')}
                                            size="small" 
                                            color="default" 
                                            variant="outlined" 
                                            sx={{ 
                                                height: 20, 
                                                fontSize: '0.7rem', 
                                                color: 'text.disabled',
                                                borderColor: 'rgba(0,0,0,0.1)' 
                                            }} 
                                        />
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        </Box>
    );
}